
import React, { useState, useEffect } from 'react';
import { Customer, PlanDetail, Subscription, SubscriptionStatus } from '../types';
import SubscriptionTable from './SubscriptionTable';
import { api } from '../services/api';


const titleize = (value: unknown, fallback: string) => {
  const text = String(value ?? "").trim();
  if (!text) return fallback;
  return text.replace(/[_-]+/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

const normalizeStatus = (status: unknown): SubscriptionStatus => {
  const normalized = String(status || SubscriptionStatus.ACTIVE).toLowerCase().replace(/[_-]+/g, " ");
  if (normalized === "past due" || normalized === "pastdue") return SubscriptionStatus.PAST_DUE;
  if (normalized === "canceled" || normalized === "cancelled") return SubscriptionStatus.CANCELED;
  if (normalized === "trial") return SubscriptionStatus.TRIAL;
  if (normalized === "paused") return SubscriptionStatus.PAUSED;
  return SubscriptionStatus.ACTIVE;
};

const normalizeBillingCycle = (cycle: unknown): 'Monthly' | 'Annually' => {
  const normalized = String(cycle || "monthly").toLowerCase();
  return normalized.startsWith("annual") || normalized.startsWith("year") ? "Annually" : "Monthly";
};

const findPlan = (plans: PlanDetail[], planRef: unknown) => {
  const target = String(planRef ?? "").trim().toLowerCase();
  if (!target) return undefined;
  return plans.find(plan => {
    const planData = plan as PlanDetail & { name?: string; title?: string; amount?: number | string };
    return [planData.id, planData.tier, planData.name, planData.title]
      .filter(Boolean)
      .some(value => String(value).trim().toLowerCase() === target);
  });
};

const findCustomer = (customers: Customer[], subscription: any) => {
  const targets = [
    subscription.customerId,
    subscription.customer_id,
    subscription.customer?.id,
    subscription.email,
    subscription.customer?.email,
  ]
    .filter(Boolean)
    .map(value => String(value).trim().toLowerCase());

  if (targets.length === 0) return undefined;

  return customers.find(customer => [customer.id, customer.email]
    .filter(Boolean)
    .some(value => targets.includes(String(value).trim().toLowerCase())));
};

const normalizeSubscription = (subscription: any, plans: PlanDetail[], customers: Customer[]): Subscription => {
  const planRef = subscription.plan != null && typeof subscription.plan === "object"
    ? subscription.plan?.id || subscription.plan?.tier || subscription.plan?.name || subscription.plan?.title
    : subscription.plan ?? subscription.planId ?? subscription.plan_id ?? subscription.tier;
  const matchedPlan = findPlan(plans, planRef);
  const matchedCustomer = findCustomer(customers, subscription);
  const billingCycle = normalizeBillingCycle(subscription.billingCycle || subscription.billing_cycle);
  const matchedPlanData = matchedPlan as (PlanDetail & { name?: string; amount?: number | string }) | undefined;
  const rawAmount = Number(subscription.mrr ?? subscription.amount ?? subscription.price ?? matchedPlanData?.price ?? matchedPlanData?.amount ?? 0);
  const mrr = Number.isFinite(rawAmount)
    ? (subscription.mrr == null && billingCycle === "Annually" ? Math.round(rawAmount / 12) : rawAmount)
    : 0;

  return {
    ...subscription,
    id: String(subscription.id || subscription.subscriptionId || subscription.gatewayRef || subscription.gateway_ref || 'sub_' + Date.now()),
    gatewayRef: String(subscription.gatewayRef || subscription.gateway_ref || subscription.subscriptionId || subscription.id || "-"),
    customerId: String(subscription.customerId || subscription.customer_id || subscription.customer?.id || matchedCustomer?.id || subscription.email || ""),
    customerName: String(matchedCustomer?.name || subscription.customer?.name || subscription.name || (subscription.customerName && subscription.customerName !== "Unknown Customer" ? subscription.customerName : "") || subscription.email || matchedCustomer?.email || "Unknown Customer"),
    plan: String(matchedPlanData?.tier || matchedPlanData?.name || subscription.planName || subscription.plan_name || (subscription.plan != null && typeof subscription.plan === "object" ? subscription.plan?.name || subscription.plan?.tier || subscription.plan?.title : "") || (String(planRef ?? "").match(/^\d+$/) ? "Custom Plan" : titleize(planRef, "Custom Plan"))),
    status: normalizeStatus(subscription.status),
    billingCycle,
    mrr,
    startDate: String(subscription.startDate || subscription.createdAt || subscription.created_at || ""),
    nextBillingDate: String(subscription.nextBillingDate || subscription.next_billing_date || subscription.current_end || "-"),
    autoRenew: subscription.autoRenew ?? subscription.auto_renew ?? true,
  };
};

interface SubscriptionsProps {
  onSubscriptionSelect?: (id: string) => void;
}

const Subscriptions: React.FC<SubscriptionsProps> = ({ onSubscriptionSelect }) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<SubscriptionStatus | 'All'>('All');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [data, plans, customers] = await Promise.all([api.subscriptions.getAll(), api.plans.getAll().catch(() => []), api.customers.getAll().catch(() => [])]);
        if (data && Array.isArray(data)) {
          setSubscriptions(data.map(subscription => normalizeSubscription(subscription, plans, customers)));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredSubscriptions = filterStatus === 'All' 
    ? subscriptions 
    : subscriptions.filter(s => s.status === filterStatus);

  const handleSelect = (sub: Subscription) => {
    if (onSubscriptionSelect) {
      onSubscriptionSelect(sub.id);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Subscription Management</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">Monitor recurring revenue streams and subscription health.</p>
        </div>
        <div className="flex gap-3">
          <div className="flex bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1 rounded-xl shadow-sm">
             {(['All', SubscriptionStatus.ACTIVE, SubscriptionStatus.PAUSED, SubscriptionStatus.PAST_DUE, SubscriptionStatus.CANCELED] as const).map(status => (
               <button 
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-1.5 text-xs font-black uppercase tracking-tight rounded-lg transition-all ${filterStatus === status ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
               >
                 {status}
               </button>
             ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-indigo-50/50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/50 p-6 rounded-2xl shadow-sm">
          <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Live Subscriptions</p>
          <p className="text-3xl font-black text-indigo-900 dark:text-indigo-300">
            {subscriptions.filter(s => s.status === SubscriptionStatus.ACTIVE).length.toLocaleString()}
          </p>
        </div>
        <div className="bg-amber-50/50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/50 p-6 rounded-2xl shadow-sm">
          <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest mb-1">Paused (Payment Failed)</p>
          <p className="text-3xl font-black text-amber-900 dark:text-amber-300">
            {subscriptions.filter(s => s.status === SubscriptionStatus.PAUSED).length.toLocaleString()}
          </p>
        </div>
        <div className="bg-red-50/50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 p-6 rounded-2xl shadow-sm">
          <p className="text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Past Due</p>
          <p className="text-3xl font-black text-red-900 dark:text-red-300">
            {subscriptions.filter(s => s.status === SubscriptionStatus.PAST_DUE).length.toLocaleString()}
          </p>
        </div>
      </div>

      {loading && subscriptions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm">
          <svg className="animate-spin h-8 w-8 text-indigo-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Scanning Recurring Pipelines...</p>
        </div>
      ) : (
        <SubscriptionTable subscriptions={filteredSubscriptions} onSelect={handleSelect} />
      )}
    </div>
  );
};

export default Subscriptions;
